import { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, Star, AlertTriangle, XCircle, CheckCircle,
  RefreshCw, MessageSquare, ChevronDown, ChevronUp,
  User, Award, Clock, BarChart2, Copy, Search, Filter,
} from 'lucide-react';
import apiService from '../services/apiService';

// ─── Helpers visuais ──────────────────────────────────────────────────────────

const TIER_CONFIG = {
  A: { label: 'Ouro',     bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  B: { label: 'Prata',    bg: 'bg-slate-50',  border: 'border-slate-400',  text: 'text-slate-700',  dot: 'bg-slate-400'  },
  C: { label: 'Bronze',   bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', dot: 'bg-orange-500' },
  D: { label: 'Risco',    bg: 'bg-red-50',    border: 'border-red-400',    text: 'text-red-700',    dot: 'bg-red-500'    },
  E: { label: 'Bloqueado',bg: 'bg-gray-100',  border: 'border-gray-400',   text: 'text-gray-600',   dot: 'bg-gray-400'   },
};

function scoreColor(s) {
  if (s >= 700) return 'text-green-700';
  if (s >= 500) return 'text-yellow-600';
  if (s >= 300) return 'text-orange-600';
  return 'text-red-600';
}
function scoreBg(s) {
  if (s >= 700) return 'bg-green-500';
  if (s >= 500) return 'bg-yellow-500';
  if (s >= 300) return 'bg-orange-500';
  return 'bg-red-500';
}

function TierBadge({ tier }) {
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.C;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function ScoreBar({ score }) {
  const pct = Math.round((score / 1000) * 100);
  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-1">
        <span className={`text-lg font-bold ${scoreColor(score)}`}>{score}</span>
        <span className="text-xs text-gray-400">/ 1000</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${scoreBg(score)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function fmt(val) {
  return (val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtData(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

// ─── Card de cliente ──────────────────────────────────────────────────────────

function ClienteCard({ cliente, onEnviarWhatsApp, enviandoId }) {
  const [expandido, setExpandido]   = useState(false);
  const [copiado, setCopiado]       = useState(false);
  const tier = TIER_CONFIG[cliente.tier] || TIER_CONFIG.C;

  function copiarMensagem() {
    if (!cliente.mensagem_whatsapp) return;
    navigator.clipboard.writeText(cliente.mensagem_whatsapp);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  const enviando = enviandoId === cliente.ordem_cliente;

  return (
    <div className={`bg-white rounded-xl border-l-4 ${tier.border} shadow-sm hover:shadow-md transition-shadow`}>
      {/* Cabeçalho */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${tier.bg}`}>
              <User className={`w-5 h-5 ${tier.text}`} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{cliente.nome}</p>
              <p className="text-xs text-gray-500">{cliente.fone || 'Sem telefone'}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <TierBadge tier={cliente.tier} />
            {cliente.elegivel_upgrade && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                <TrendingUp size={10} /> Elegível
              </span>
            )}
          </div>
        </div>

        {/* Score bar */}
        <div className="mt-3">
          <ScoreBar score={cliente.score} />
        </div>

        {/* Métricas rápidas */}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded-lg py-2 px-1">
            <p className="text-xs text-gray-500">Pagas</p>
            <p className="font-bold text-gray-900">{cliente.total_pagas}</p>
          </div>
          <div className="bg-gray-50 rounded-lg py-2 px-1">
            <p className="text-xs text-gray-500">Atrasos</p>
            <p className={`font-bold ${cliente.qtd_atrasos > 5 ? 'text-red-600' : 'text-gray-900'}`}>{cliente.qtd_atrasos}</p>
          </div>
          <div className="bg-gray-50 rounded-lg py-2 px-1">
            <p className="text-xs text-gray-500">Maior atraso</p>
            <p className={`font-bold ${cliente.maior_atraso_dias > 15 ? 'text-red-600' : 'text-gray-900'}`}>{cliente.maior_atraso_dias}d</p>
          </div>
        </div>

        {/* Novo limite (se elegível) */}
        {cliente.elegivel_upgrade && cliente.novo_limite_sugerido && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600 font-medium">Novo limite sugerido</p>
              <p className="text-lg font-bold text-green-700">{fmt(cliente.novo_limite_sugerido)}</p>
            </div>
            <Award className="w-8 h-8 text-green-400" />
          </div>
        )}

        {/* Motivo inelegível */}
        {!cliente.elegivel_upgrade && cliente.motivo_inelegivel && (
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
            <p className="text-xs text-gray-500">{cliente.motivo_inelegivel}</p>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="px-4 pb-3 flex items-center gap-2">
        {cliente.elegivel_upgrade && cliente.mensagem_whatsapp && (
          <>
            <button
              onClick={() => onEnviarWhatsApp(cliente)}
              disabled={enviando || !cliente.fone}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enviando ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <MessageSquare size={14} />
              )}
              {enviando ? 'Enviando…' : 'Enviar WhatsApp'}
            </button>
            <button
              onClick={copiarMensagem}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Copiar mensagem"
            >
              {copiado ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16} className="text-gray-500" />}
            </button>
          </>
        )}
        <button
          onClick={() => setExpandido(e => !e)}
          className="ml-auto p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          title={expandido ? 'Recolher' : 'Ver detalhes'}
        >
          {expandido ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
        </button>
      </div>

      {/* Detalhes expandidos */}
      {expandido && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Saldo devedor</p>
              <p className="font-semibold text-gray-800">{fmt(cliente.saldo_devedor)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total crediário (2 anos)</p>
              <p className="font-semibold text-gray-800">{fmt(cliente.valor_total)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Crediários distintos</p>
              <p className="font-semibold text-gray-800">{cliente.qtd_crediarios}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Parcela média</p>
              <p className="font-semibold text-gray-800">{fmt(cliente.valor_medio_parcela)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Média de atraso</p>
              <p className="font-semibold text-gray-800">{cliente.media_atraso_dias} dias</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Último pagamento</p>
              <p className="font-semibold text-gray-800">{fmtData(cliente.ultima_quitacao)}</p>
            </div>
          </div>

          {/* Mensagem WhatsApp preview */}
          {cliente.mensagem_whatsapp && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-3">
              <p className="text-xs font-semibold text-green-700 mb-1">Mensagem WhatsApp</p>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{cliente.mensagem_whatsapp}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Cards de resumo ──────────────────────────────────────────────────────────

function ResumoCards({ clientes }) {
  const elegíveis  = clientes.filter(c => c.elegivel_upgrade).length;
  const ouro       = clientes.filter(c => c.tier === 'A').length;
  const risco      = clientes.filter(c => c.tier === 'D' || c.tier === 'E').length;
  const total      = clientes.length;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {[
        { label: 'Total analisados', value: total,     icon: BarChart2,   color: 'blue'   },
        { label: 'Tier Ouro',        value: ouro,      icon: Star,        color: 'yellow' },
        { label: 'Elegíveis upgrade',value: elegíveis, icon: TrendingUp,  color: 'green'  },
        { label: 'Clientes em risco',value: risco,     icon: AlertTriangle,color: 'red'   },
      ].map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">{label}</p>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${color}-50`}>
              <Icon className={`w-4 h-4 text-${color}-600`} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function CrediarioRotativo() {
  const [clientes, setClientes]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [erro, setErro]               = useState('');
  const [busca, setBusca]             = useState('');
  const [filtroTier, setFiltroTier]   = useState('todos');
  const [filtroEleg, setFiltroEleg]   = useState('todos');
  const [enviandoId, setEnviandoId]   = useState(null);
  const [toast, setToast]             = useState(null);

  const [percentualLiberacao, setPercentualLiberacao] = useState(80);
  const [percentualEdicao, setPercentualEdicao]       = useState('');
  const [salvandoPercentual, setSalvandoPercentual]   = useState(false);

  function showToast(msg, tipo = 'success') {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 4000);
  }

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.get('/api/credito-rotativo/clientes?porPagina=200');
      setClientes(resp.data.clientes || []);
    } catch (e) {
      setErro('Erro ao carregar clientes. Verifique a conexão com o backend.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarPercentual = useCallback(async () => {
    try {
      const resp = await apiService.get('/api/credito-rotativo/percentual-liberacao');
      setPercentualLiberacao(resp.data.percentual ?? 80);
    } catch {
      // silencioso — mantém o padrão de 80% já usado como fallback
    }
  }, []);

  useEffect(() => { carregar(); carregarPercentual(); }, [carregar, carregarPercentual]);

  async function salvarPercentual() {
    const valor = Number(percentualEdicao);
    if (!Number.isFinite(valor) || valor <= 0 || valor > 100) {
      showToast('Percentual deve ser um número entre 0 e 100.', 'error');
      return;
    }
    setSalvandoPercentual(true);
    try {
      const resp = await apiService.put('/api/credito-rotativo/percentual-liberacao', { percentual: valor });
      setPercentualLiberacao(resp.data.percentual);
      setPercentualEdicao('');
      showToast('Percentual atualizado com sucesso!');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao salvar percentual.', 'error');
    } finally {
      setSalvandoPercentual(false);
    }
  }

  async function enviarWhatsApp(cliente) {
    if (!cliente.fone) {
      showToast('Cliente sem telefone cadastrado.', 'error');
      return;
    }
    setEnviandoId(cliente.ordem_cliente);
    try {
      const resp = await apiService.post('/api/credito-rotativo/enviar-notificacao', {
        ordem_cliente:  cliente.ordem_cliente,
        nome:           cliente.nome,
        fone:           cliente.fone,
        mensagem:       cliente.mensagem_whatsapp,
        score:          cliente.score,
        tier:           cliente.tier,
        novo_limite:    cliente.novo_limite_sugerido,
        enviado_por:    'usuario',
      });
      if (resp.data.success) {
        showToast(`Mensagem enviada para ${cliente.nome.split(' ')[0]}!`);
      } else {
        showToast(`Falha ao enviar: ${resp.data.error || 'Erro desconhecido'}`, 'error');
      }
    } catch (e) {
      showToast('Erro ao enviar mensagem WhatsApp.', 'error');
    } finally {
      setEnviandoId(null);
    }
  }

  // Filtros e busca
  const clientesFiltrados = clientes.filter(c => {
    const matchBusca = !busca || c.nome?.toLowerCase().includes(busca.toLowerCase()) || c.fone?.includes(busca);
    const matchTier  = filtroTier === 'todos' || c.tier === filtroTier;
    const matchEleg  = filtroEleg === 'todos'
      || (filtroEleg === 'elegiveis' && c.elegivel_upgrade)
      || (filtroEleg === 'nao_elegiveis' && !c.elegivel_upgrade);
    return matchBusca && matchTier && matchEleg;
  });

  // Ordenação: elegíveis primeiro, depois por score desc
  const clientesOrdenados = [...clientesFiltrados].sort((a, b) => {
    if (a.elegivel_upgrade !== b.elegivel_upgrade) return a.elegivel_upgrade ? -1 : 1;
    return b.score - a.score;
  });

  return (
    <div className="max-w-7xl mx-auto">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all
          ${toast.tipo === 'success' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
          {toast.tipo === 'success'
            ? <CheckCircle size={16} className="text-green-600" />
            : <XCircle size={16} className="text-red-600" />}
          {toast.msg}
        </div>
      )}

      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crediário Rotativo</h1>
          <p className="text-sm text-gray-500 mt-0.5">Score comportamental + elegibilidade de upgrade automático</p>
        </div>
        <button
          onClick={carregar}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Calculando…' : 'Recalcular scores'}
        </button>
      </div>

      {/* Erro */}
      {erro && (
        <div className="mb-4 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle size={16} className="flex-shrink-0" />
          {erro}
        </div>
      )}

      {/* Cards de resumo */}
      {clientes.length > 0 && <ResumoCards clientes={clientes} />}

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone…"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter size={15} className="text-gray-400 flex-shrink-0" />
          <select
            value={filtroTier}
            onChange={e => setFiltroTier(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="todos">Todos os tiers</option>
            <option value="A">Ouro</option>
            <option value="B">Prata</option>
            <option value="C">Bronze</option>
            <option value="D">Risco</option>
            <option value="E">Bloqueado</option>
          </select>

          <select
            value={filtroEleg}
            onChange={e => setFiltroEleg(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="todos">Todos</option>
            <option value="elegiveis">Elegíveis para upgrade</option>
            <option value="nao_elegiveis">Não elegíveis</option>
          </select>
        </div>
      </div>

      {/* Contagem */}
      {!loading && clientes.length > 0 && (
        <p className="text-sm text-gray-500 mb-3">
          Exibindo <strong>{clientesOrdenados.length}</strong> de <strong>{clientes.length}</strong> clientes
        </p>
      )}

      {/* Estado de loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <RefreshCw size={32} className="animate-spin mb-4 text-blue-500" />
          <p className="font-medium text-gray-600">Calculando scores…</p>
          <p className="text-sm mt-1">Consultando histórico de crediário dos clientes</p>
        </div>
      )}

      {/* Estado vazio */}
      {!loading && clientes.length === 0 && !erro && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <BarChart2 size={48} className="mb-4 text-gray-300" />
          <p className="font-medium text-gray-600">Nenhum cliente encontrado</p>
          <p className="text-sm mt-1">Clique em "Recalcular scores" para carregar</p>
        </div>
      )}

      {/* Grid de cards */}
      {!loading && clientesOrdenados.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clientesOrdenados.map(c => (
            <ClienteCard
              key={c.ordem_cliente}
              cliente={c}
              onEnviarWhatsApp={enviarWhatsApp}
              enviandoId={enviandoId}
            />
          ))}
        </div>
      )}

      {/* Sem resultados após filtro */}
      {!loading && clientes.length > 0 && clientesOrdenados.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Search size={32} className="mb-3 text-gray-300" />
          <p className="font-medium text-gray-600">Nenhum cliente corresponde ao filtro</p>
          <button onClick={() => { setBusca(''); setFiltroTier('todos'); setFiltroEleg('todos'); }}
            className="mt-3 text-sm text-blue-600 hover:underline">
            Limpar filtros
          </button>
        </div>
      )}

      {/* Legenda dos tiers */}
      <div className="mt-8 bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Critérios de classificação</p>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 text-xs">
          {[
            { tier: 'A', desc: 'Score ≥ 700, sem atraso > 5 dias → Upgrade automático' },
            { tier: 'B', desc: 'Score ≥ 500, atraso ≤ 15 dias → Upgrade condicional' },
            { tier: 'C', desc: 'Score ≥ 300, atrasos moderados → Manutenção do limite' },
            { tier: 'D', desc: 'Score ≥ 150, atrasos frequentes → Redução preventiva' },
            { tier: 'E', desc: 'Score < 150 ou atraso > 30 dias → Bloqueio' },
          ].map(({ tier, desc }) => {
            const cfg = TIER_CONFIG[tier];
            return (
              <div key={tier} className={`rounded-lg p-2.5 border ${cfg.bg} ${cfg.border}`}>
                <TierBadge tier={tier} />
                <p className="mt-1.5 text-gray-600">{desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Configuração do alerta por email de crédito liberado */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Alerta por email</p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
            Enviar email de aviso de crédito liberado após
            <input
              type="number"
              min="1"
              max="100"
              placeholder={String(percentualLiberacao)}
              value={percentualEdicao}
              onChange={e => setPercentualEdicao(e.target.value)}
              className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-blue-400"
            />
            % de pago os valores a receber.
          </label>
          <button
            onClick={salvarPercentual}
            disabled={!percentualEdicao || salvandoPercentual}
            className="px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {salvandoPercentual ? 'Salvando...' : 'Salvar'}
          </button>
          <span className="text-xs text-gray-400">Valor atual: {percentualLiberacao}%</span>
        </div>
      </div>
    </div>
  );
}
